import React, { useState, useEffect } from 'react';
import ColaboradorLayout from '../../components/ColaboradorLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { assignmentsAPI } from '../../services/api';
import { Calendar, CheckCircle, Loader2 } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { format, startOfWeek, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const ColaboradorCalendarPage = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const todayChile = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santiago' });

  // Parse "YYYY-MM-DD" as local date (not UTC)
  const parseLocalDate = (dateStr) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  useEffect(() => {
    loadAssignments();
  }, []);

  const loadAssignments = async () => {
    try {
      const response = await assignmentsAPI.getAll({});
      setAssignments(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (id) => {
    try {
      await assignmentsAPI.confirm(id);
      toast.success('Asistencia confirmada');
      loadAssignments();
    } catch (error) {
      toast.error('Error al confirmar');
    }
  };

  // Calendar generation
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
  const endDate = addDays(startOfWeek(monthEnd, { weekStartsOn: 1 }), 6);
  const days = eachDayOfInterval({ start: startDate, end: endDate });

  const getAssignmentForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return assignments.find(a => a.date === dateStr);
  };

  const prevMonth = () => setCurrentMonth(addDays(monthStart, -1));
  const nextMonth = () => setCurrentMonth(addDays(monthEnd, 1));

  const selectedAssignment = selectedDate ? getAssignmentForDate(selectedDate) : null;

  return (
    <ColaboradorLayout>
      <Toaster position="top-right" />
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Mi Calendario
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Vista de tus días asignados de asistencia presencial
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar */}
            <Card className="border border-slate-200 lg:col-span-2">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <Button variant="ghost" onClick={prevMonth} className="rounded-sm">&lt;</Button>
                  <CardTitle className="text-lg capitalize">
                    {format(currentMonth, 'MMMM yyyy', { locale: es })}
                  </CardTitle>
                  <Button variant="ghost" onClick={nextMonth} className="rounded-sm">&gt;</Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Days header */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((d) => (
                    <div key={d} className="text-center text-xs font-medium text-slate-500 py-2">
                      {d}
                    </div>
                  ))}
                </div>
                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1">
                  {days.map((day) => {
                    const assignment = getAssignmentForDate(day);
                    const isCurrentMonth = isSameMonth(day, currentMonth);
                    const isSelected = selectedDate && isSameDay(day, selectedDate);
                    
                    return (
                      <div
                        key={day.toISOString()}
                        onClick={() => setSelectedDate(day)}
                        className={`calendar-cell cursor-pointer relative ${
                          !isCurrentMonth ? 'bg-slate-50 text-slate-400' : ''
                        } ${isToday(day) ? 'today' : ''} ${isSelected ? 'ring-2 ring-[#002FA7]' : ''}`}
                      >
                        <span className="text-sm">{format(day, 'd')}</span>
                        {assignment && (
                          <div className={`absolute bottom-1 left-1 right-1 h-2 rounded-sm ${
                            assignment.status === 'confirmed' ? 'bg-green-500' : 'bg-[#002FA7]'
                          }`} />
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Legend */}
                <div className="flex items-center gap-4 mt-4 text-xs text-slate-500">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-[#002FA7] rounded-sm" />
                    <span>Asignado</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-green-500 rounded-sm" />
                    <span>Confirmado</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Selected Day Details */}
            <Card className="border border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">
                  {selectedDate ? format(selectedDate, 'EEEE d MMMM', { locale: es }) : 'Selecciona un día'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedAssignment ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-sm">
                      <p className="font-medium text-[#002FA7]">Asistencia Presencial</p>
                      <p className="text-sm text-slate-600 mt-1">
                        Estado: <span className={selectedAssignment.status === 'confirmed' ? 'text-green-600' : 'text-blue-600'}>
                          {selectedAssignment.status === 'confirmed' ? 'Confirmado' : 'Pendiente de confirmar'}
                        </span>
                      </p>
                      {selectedAssignment.space_name && (
                        <p className="text-sm text-slate-600">Espacio: {selectedAssignment.space_name}</p>
                      )}
                      {selectedAssignment.assignment_reason && (
                        <p className="text-xs text-slate-500 mt-2">{selectedAssignment.assignment_reason}</p>
                      )}
                    </div>
                    {selectedAssignment.status === 'assigned' && (
                      <Button 
                        onClick={() => handleConfirm(selectedAssignment.id)}
                        className="w-full bg-[#002FA7] hover:bg-[#001B66] rounded-sm"
                        data-testid="confirm-attendance-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Confirmar Asistencia
                      </Button>
                    )}
                  </div>
                ) : selectedDate ? (
                  <div className="text-center py-8 text-slate-500">
                    <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p>No tienes asignación para este día</p>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p>Selecciona un día del calendario</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Upcoming Assignments */}
        <Card className="border border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Próximas Asignaciones</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {assignments.filter(a => a.date >= todayChile()).slice(0, 5).map((a) => (
                <div key={a.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-sm border border-slate-200">
                  <div>
                    <p className="font-medium">{format(parseLocalDate(a.date), 'EEEE d MMMM', { locale: es })}</p>
                    <span className={a.status === 'confirmed' ? 'badge-success' : 'badge-info'}>
                      {a.status === 'confirmed' ? 'Confirmado' : 'Asignado'}
                    </span>
                  </div>
                  {a.status === 'assigned' && (
                    <Button 
                      size="sm" 
                      onClick={() => handleConfirm(a.id)}
                      className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm"
                    >
                      Confirmar
                    </Button>
                  )}
                </div>
              ))}
              {assignments.filter(a => a.date >= todayChile()).length === 0 && (
                <p className="text-center text-slate-500 py-4">No tienes asignaciones próximas</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </ColaboradorLayout>
  );
};

export default ColaboradorCalendarPage;
